import {
    FeatureNames,
    useFeatures,
} from '@paralleldrive/react-feature-toggles';
import { TFunction } from 'i18next';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { isSingleApplication } from '../../common/Briefing';
import { Customer } from '../../common/Customer';
import { Button } from '../../components/Button';
import { Dropdown } from '../../components/Dropdown';
import { FilterGroup } from '../../components/FilterGroup';
import { FormulationTile } from '../../components/FormulationTile';
import { Heading } from '../../components/Heading';
import { Input } from '../../components/Input';
import { Link } from '../../components/Link';
import { LinkButton } from '../../components/LinkButton';
import { Loading } from '../../components/Loading';
import { TabGroup } from '../../components/TabGroup';
import { Tab } from '../../components/TabGroup/TabGroup';
import { GTMBaseContentViewObject, PageTracker } from '../../hooks/GTMUtils';
import { useBriefings } from '../../hooks/queries';
import { useAppRoutes } from '../../hooks/useAppRoutes';
import { useAuth } from '../../hooks/useAuth';
import {
    CountryMarketHelpers,
    useCountryMarketHelpers,
} from '../../hooks/useCountryMarketHelpers';
import { useGTM } from '../../hooks/useGTM';
import {
    DropdownOption,
    DropdownOptionsWithUndefined,
} from '../../lib/Dropdown/types';
import { noFilter, noSort } from '../../lib/utils';
import { User } from '../../shared/store';
import { Briefing, TrackingSchema } from '../../types';

export const amountOptions = [
  { name: '12', value: 12 },
  { name: '24', value: 24 },
  { name: '48', value: 48 },
  { name: '96', value: 96 },
];

enum FilterNames {
  Country = 'country',
  MarketSegment = 'market',
  Application = 'application',
}

const isFilterName = (filter: string): filter is FilterNames =>
  Object.values(FilterNames).includes(filter as FilterNames);

type ProjectFilter = {
  key: FilterNames;
  select: (briefing: Briefing, option: DropdownOption) => boolean;
  collect: (briefing: Briefing) => string | null;
  display: (value: string | null, t: TFunction) => string | null;
};

const filterOptions: { [key in FilterNames]: ProjectFilter } = {
  [FilterNames.Country]: {
    key: FilterNames.Country,
    select: ({ country }, { value }) => value === country,
    collect: ({ country }) => country,
    display: (value, t) => t(`labels:countrySlug:${value}`),
  },
  [FilterNames.MarketSegment]: {
    key: FilterNames.MarketSegment,
    select: ({ marketSegment }, { value }) => value === marketSegment,
    collect: ({ marketSegment }) => marketSegment,
    display: (value, t) => t(`labels:markets:${value}`),
  },
  [FilterNames.Application]: {
    key: FilterNames.Application,
    select: ({ applications: [app] }, { value }) => {
      const application = isSingleApplication(app) ? app : app['0'];
      return application.data?.trim() === value;
    },
    collect: ({ applications: [app] }) => {
      if (app) {
        const application = isSingleApplication(app) ? app : app['0'];
        return application.data?.trim() ?? '';
      } else {
        return null;
      }
    },
    display: (value) => value,
  },
};

type FilterValues = { key: FilterNames; values: Set<string | null> }[];

type TabSettings = {
  key: string;
  name: (customer?: Customer | null) => string;
  isVisible: (features: FeatureNames) => boolean;
  preFilter: (briefing: Briefing) => boolean;
  pageNumber: number;
  pageSize: number;
  total: number;
  selectedFilters: { [key in FilterNames]?: DropdownOptionsWithUndefined };
};
export const defaultTabSettings: TabSettings = {
  key: '',
  name: () => '',
  isVisible: () => true,
  preFilter: noFilter,
  pageNumber: 1,
  pageSize: amountOptions[0].value,
  total: 0,
  selectedFilters: {},
};

const sortByCreatedDate = (a: Briefing, b: Briefing) =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

const applyFilters = (tab: TabSettings, br: Briefing): boolean =>
  Object.values(FilterNames)
    .map((filter) => {
      const selectedFilter = [tab.selectedFilters[filter]]
        .flat()
        .filter((o): o is DropdownOption => !!o);
      return (
        !selectedFilter.length ||
        selectedFilter.some(filterOptions[filter].select.bind(null, br))
      );
    })
    .every((result) => result);

const filterBriefingsForTab = (briefings: Briefing[], tab: TabSettings) =>
  briefings.filter((br) => applyFilters(tab, br)).sort(sortByCreatedDate);

/**
 * Filter the briefings to only show the briefings that the
 * displayUser has access to from countries_and_markets
 */

const filterBriefingsByUserCountryMarketSegments = (
  briefings: Briefing[],
  user: User | undefined,
  { findCountryMarketSlugsByStringsArray }: CountryMarketHelpers,
) =>
  findCountryMarketSlugsByStringsArray(user?.countries_and_markets || {})
    .map((countryMarketSlugsConfig) =>
      briefings.filter(
        (briefing) =>
          briefing.country === countryMarketSlugsConfig.country &&
          briefing.marketSegment === countryMarketSlugsConfig.market,
      ),
    )
    .flat();

export const ProjectOverview: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { briefingSelectionRoute } = useAppRoutes();
  const featureNames = useFeatures();
  const [{ displayUser, customer }] = useAuth();
  const countryMarketHelpers = useCountryMarketHelpers();

  const [tabsSettings, setTabsSettings] = useState<TabSettings[]>([
    {
      ...defaultTabSettings,
      key: 'my-projects',
      name: () => t('components:projectOverview:myFormulations'),
      preFilter: (br) => br.ownedBy?.uid === displayUser?.uid,
      selectedFilters: {
        ...defaultTabSettings.selectedFilters,
      },
    },
    {
      ...defaultTabSettings,
      key: 'team-projects',
      name: (customer) =>
        t('components:projectOverview:teamFormulations', {
          team: customer?.customer_name || t('components:projectOverview:team'),
        }),
      isVisible: (features) => features.includes('team-briefings'),
      selectedFilters: {
        ...defaultTabSettings.selectedFilters,
      },
    },
  ]);

  const setTabInTabsSettings = useCallback(
    (tab: TabSettings) => {
      setTabsSettings(
        tabsSettings.map((tabSettings) =>
          tabSettings.key === tab.key ? tab : tabSettings,
        ),
      );
    },
    [tabsSettings],
  );

  const getTabSettingsByKey = useCallback(
    (key: string) => tabsSettings.find((tab) => tab.key === key),
    [tabsSettings],
  );

  const { briefings, isLoading: isBriefingsLoading } = useBriefings();

  const isLoading = useMemo(
    () => isBriefingsLoading || !displayUser,
    [isBriefingsLoading, displayUser],
  );

  const storeBriefings = useMemo(
    () =>
      Object.entries(briefings)
        .filter(([, briefing]) => !!briefing)
        .map(([key, briefing]) => ({
          ...briefing,
          key,
        })),
    [briefings],
  );

  const briefingsForTab = useCallback(
    (tab: TabSettings) => storeBriefings.filter(tab.preFilter),
    [storeBriefings],
  );

  const pushToHistory = useCallback(
    (
      key: keyof typeof location,
      value: (typeof location)[keyof typeof location],
    ) => {
      navigate({
        ...location,
        [key]: value,
      });
    },
    [navigate, location],
  );

  const addSearchParam = useCallback(
    (key: string, value: unknown) => {
      const queryParams = new URLSearchParams(location.search);
      queryParams.set(key, JSON.stringify(value));
      pushToHistory('search', queryParams.toString());
    },
    [location.search, pushToHistory],
  );

  const deleteSearchParam = useCallback(
    (keys: string | string[]) => {
      keys = [keys].flat();
      const queryParams = new URLSearchParams(location.search);
      keys.forEach((key) => {
        queryParams.delete(key);
      });
      pushToHistory('search', queryParams.toString());
    },
    [location.search, pushToHistory],
  );

  const handleSearchParam = useCallback(
    (key: string, value: DropdownOptionsWithUndefined) => {
      if (value === undefined || ![value].flat().length) {
        deleteSearchParam(key);
      } else {
        addSearchParam(key, value);
      }
    },
    [addSearchParam, deleteSearchParam],
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    for (const [key, value] of queryParams.entries()) {
      const [tabKey, filter] = key.split('|');
      const tab = getTabSettingsByKey(tabKey);

      if (!tab || !isFilterName(filter)) continue;

      tab.selectedFilters[filter] = JSON.parse(value);
    }
  }, [getTabSettingsByKey, location.search, setTabInTabsSettings]);

  const setFilterPage = useCallback(
    (tab: TabSettings, pageIndex: number, maxPage: number) => {
      if (!pageIndex || pageIndex > maxPage) return;

      tab.pageNumber = pageIndex;

      setTabInTabsSettings(tab);
    },
    [setTabInTabsSettings],
  );

  const changeFilterOptions = useCallback(
    (
      tab: TabSettings,
      filterName: FilterNames,
      selectedOptions: DropdownOptionsWithUndefined,
    ) => {
      tab.selectedFilters[filterName] = selectedOptions;

      handleSearchParam(`${tab.key}|${filterName}`, selectedOptions);

      setTabInTabsSettings(tab);
      setFilterPage(tab, 1, 1);
    },
    [handleSearchParam, setTabInTabsSettings, setFilterPage],
  );
  const resetFilterOptions = useCallback(
    (tab: TabSettings) => {
      deleteSearchParam(
        Object.keys(tab.selectedFilters).map(
          (filterName) => `${tab.key}|${filterName}`,
        ),
      );
      tab.selectedFilters = {};

      setTabInTabsSettings(tab);
      setFilterPage(tab, 1, 1);
    },
    [deleteSearchParam, setFilterPage, setTabInTabsSettings],
  );

  const generatePagination = useCallback(
    (tab: TabSettings, maxPage: number) => (
      <div className="flex items-center font-regular">
        <LinkButton
          direction="backward"
          label={
            <span className="hidden sm:inline">
              {t('translation:linksOrButtons:prev')}
            </span>
          }
          disabled={tab.pageNumber === 1}
          onClick={() => setFilterPage(tab, tab.pageNumber - 1, maxPage)}
        />
        <div className="flex items-center mx-3">
          <div
            style={{
              maxWidth: '70px',
            }}
            className="sm:mr-2"
          >
            <Input
              initialValue={tab.pageNumber}
              type="number"
              maxNumber={maxPage}
              minNumber={1}
              onChange={(event) =>
                setFilterPage(tab, parseInt(event.currentTarget.value), maxPage)
              }
            />
          </div>
          <span className="hidden sm:inline">
            {t('translation:labels:ofTotal', { total: maxPage })}
          </span>
        </div>
        <LinkButton
          direction="forward"
          disabled={tab.pageNumber >= maxPage}
          label={
            <span className="hidden sm:inline">
              {t('translation:linksOrButtons:next')}
            </span>
          }
          onClick={() => setFilterPage(tab, tab.pageNumber + 1, maxPage)}
        />
      </div>
    ),
    [setFilterPage, t],
  );

  const generateFilters = useCallback(
    (tab: TabSettings, filters: FilterValues) =>
      filters.map(({ key, values }) => (
        <div data-testid={`${key}-filter`} key={`${key}-filter`}>
          <Dropdown
            options={[...values]
              .filter((value) => value)
              .map(
                (value) =>
                  ({
                    name: filterOptions[key].display(value, t),
                    value,
                  } as DropdownOption),
              )}
            multiselect
            placeholder={t(`components:projectOverview:filter:${key}`)}
            initialSelection={tab.selectedFilters[key]}
            onChange={(selectedOptions) => {
              changeFilterOptions(tab, key, selectedOptions);
            }}
          />
        </div>
      )),
    [changeFilterOptions, t],
  );

  const generateFormulations = useCallback(
    (tab: TabSettings) => {
      if (isLoading) {
        return (
          <div className="mt-4">
            <Loading />
          </div>
        );
      }
      const tabBriefings = briefingsForTab(tab);
      const filteredTabBriefings = filterBriefingsForTab(
        filterBriefingsByUserCountryMarketSegments(
          tabBriefings,
          displayUser,
          countryMarketHelpers,
        ),
        tab,
      );
      const maxPage =
        Math.ceil(filteredTabBriefings.length / tab.pageSize) || 1;

      const availableFilters: FilterValues = Object.values(filterOptions).map(
        ({ key, collect }) => ({
          key,
          values: new Set(tabBriefings.map(collect).filter((val) => val)),
        }),
      );

      return (
        <>
          <div className="relative flex flex-row items-start justify-between mb-8 lg:grid-cols-3 pt-12">
            <FilterGroup
              isActive={filteredTabBriefings.length !== tabBriefings.length}
            >
              <div className="grid grid-cols-2 gap-6 xl:max-w-xl-content md:grid-cols-3 xs:gap-9">
                {generateFilters(tab, availableFilters)}
              </div>
              <div className="pt-2 text-xs text-right">
                <Link
                  label={t('components:projectOverview:resetAllFilters')}
                  icon="Cancel"
                  onClick={() => resetFilterOptions(tab)}
                />
              </div>
            </FilterGroup>
            {generatePagination(tab, maxPage)}
            <div
              className="flex flex-row items-center"
              data-testid="page-size-filter"
            >
              <label className="flex-shrink-0 hidden mr-1 text-gray-600 whitespace-nowrap small md:inline">
                {t('translation:labels:perPage')}
              </label>
              <Dropdown
                options={amountOptions}
                initialSelection={amountOptions.find(
                  (amOpt) => amOpt.value === tab.pageSize,
                )}
                compact={true}
                hideCheckmark={true}
                onSort={noSort}
                onChange={(selectedOption) => {
                  const newAmount = Array.isArray(selectedOption)
                    ? selectedOption[0].value
                    : selectedOption?.value;
                  if (!newAmount || typeof newAmount === 'string') return;

                  tab.pageSize = newAmount;
                  tab.pageNumber = 1;
                  setTabInTabsSettings(tab);
                }}
              />
            </div>
          </div>
          {filteredTabBriefings.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTabBriefings
                .slice(
                  (tab.pageNumber - 1) * tab.pageSize,
                  tab.pageNumber * tab.pageSize,
                )
                .filter((_, index) => index <= tab.pageSize)
                .map((briefing) => (
                  <div key={briefing.key} data-testid="formulation">
                    <FormulationTile briefing={briefing} />
                  </div>
                ))}
            </div>
          ) : (
            <div className="my-10 text-center text-gray-600">
              {t('components:projectOverview:emptyFormulations')}
            </div>
          )}
          <div className="flex justify-center my-6">
            {generatePagination(tab, maxPage)}
          </div>
        </>
      );
    },
    [
      isLoading,
      briefingsForTab,
      displayUser,
      countryMarketHelpers,
      generateFilters,
      t,
      generatePagination,
      resetFilterOptions,
      setTabInTabsSettings,
    ],
  );

  const projectOverviewTabs = useMemo<Tab[]>(
    () =>
      tabsSettings
        .filter(({ isVisible }) => isVisible(featureNames))
        .map((tab) => ({
          key: tab.key,
          name: tab.name(customer),
          content: generateFormulations(tab),
        })),
    [tabsSettings, featureNames, customer, generateFormulations],
  );

  const findIndexOfHashLocation = useCallback(
    (tabs: Tab[]) =>
      tabs.findIndex(({ key }) => key === location.hash.slice(1)),
    [location],
  );

  const { updateDataLayer } = useGTM();

  const gtmTrackingData = {
    ...GTMBaseContentViewObject,
    data: {
      category: 'User Area',
      type: 'List',
      content: 'Formulations Overview',
    },
  };

  return (
    <PageTracker gtmTrackingData={gtmTrackingData}>
      <div className="flex flex-col flex-grow max-w-6xl">
        <div className="mx-8 md:mx-16 mt-12 sm:mt-16">
          <div className="w-full">
            <div className="flex flex-col justify-between md:flex-row">
              <Heading
                text={`${t('components:projectOverview:heading')}`}
                margin={1}
                level="h1"
              />
              <div className="mt-7 ml-0 sm:mt-5 md:mt-0 md:ml-2">
                <Button onClick={() => navigate(briefingSelectionRoute)}>
                  {t('components:projectOverview:createFormulation')}
                </Button>
              </div>
            </div>
          </div>
          <div className="mt-12 md:mt-20">
            <TabGroup
              tabs={projectOverviewTabs}
              initialActiveIndex={findIndexOfHashLocation(projectOverviewTabs)}
              onTabSwitch={(tab) => {
                if (tab.key === 'team-projects') {
                  updateDataLayer({
                    event: 'Interaction',
                    schema: TrackingSchema.interaction,
                    data: {
                      group: 'Formulation',
                      name: 'Company Check',
                      context: 'Formulations Overview',
                    },
                  });
                }

                pushToHistory('hash', `#${tab.key}`);
              }}
            />
          </div>
        </div>
      </div>
    </PageTracker>
  );
};
